import { apiFetchWithRetry } from './apiFetch';

export type UserRole = 'STUDENT' | 'SCHOOL_ADMIN' | 'ADMIN';
export type UserStatus = 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'DELETED';
export type OnboardingStatus = 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED';
export type SchoolRoleValue = 'OWNER' | 'BILLING' | 'ADMIN' | 'COUNSELOR' | 'CLASSROOM_VIEWER';

export interface UserListItem {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: UserRole;
  status: UserStatus;
  slug: string | null;
  schoolName: string | null;
  onboardingStatus: OnboardingStatus | null;
  /** ISO 8601 string (Date serializes through JSON as a string). */
  createdAt: string;
  achievementsCount: number;
  /**
   * Only present on the PATCH .../role response. Anything other than 'ok'
   * means the DB role changed but the user's live Clerk identity was NOT
   * updated (missing/stale auth_user_id) — surface clerkSyncMessage so the
   * operator knows the change may not take effect at sign-in.
   */
  clerkSync?: 'ok' | 'skipped_no_clerk_id' | 'failed';
  clerkSyncMessage?: string;
}

export interface UserListResponse {
  users: UserListItem[];
  total: number;
  page: number;
  pageSize: number;
}

// Sortable columns for the Users table. Mirrors ADMIN_USER_SORT_KEYS in
// the backend dashboard.service. Sorting is server-side, so it applies
// across all matching users, not just the visible page.
export type UserSortBy = 'name' | 'email' | 'role' | 'status' | 'school' | 'createdAt';

export interface UserListQuery {
  q?: string;
  role?: UserRole;
  status?: UserStatus;
  /** Column to sort by, server-side. Defaults to `createdAt`. */
  sortBy?: UserSortBy;
  /** Sort direction. Defaults to `desc`. */
  sortDir?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}

export const userManagementService = {
  /**
   * Fetch a page of users with optional search and filter. Admin-only on the
   * server (returns 403 for non-admins). The retry wrapper signs the user
   * out if they end up holding a non-admin token here.
   */
  listUsers: async (query: UserListQuery = {}): Promise<UserListResponse> => {
    const params = new URLSearchParams();
    if (query.q && query.q.trim()) params.set('q', query.q.trim());
    if (query.role) params.set('role', query.role);
    if (query.status) params.set('status', query.status);
    if (query.sortBy) params.set('sortBy', query.sortBy);
    if (query.sortDir) params.set('sortDir', query.sortDir);
    if (query.page) params.set('page', String(query.page));
    if (query.pageSize) params.set('pageSize', String(query.pageSize));
    const qs = params.toString();
    const response = await apiFetchWithRetry(`/v1/dashboard/admins/users${qs ? `?${qs}` : ''}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch users (${response.status})`);
    }
    return (await response.json()) as UserListResponse;
  },

  /**
   * Change a user's platform role. Handles all transitions:
   *   - STUDENT ↔ ADMIN: pass role only.
   *   - * → SCHOOL_ADMIN: pass role + schoolId (and optional schoolRole,
   *     defaults server-side to ADMIN). Server upserts the
   *     SchoolMembership row.
   *   - SCHOOL_ADMIN → STUDENT/ADMIN: pass role only. Server deactivates
   *     existing memberships and clears the user's schoolId.
   *
   * Server rejects self-demotion (would lock the caller out and the
   * retry wrapper would sign them out). Throws with the server's error
   * message so the UI can surface it.
   */
  updateUserRole: async (
    id: string,
    payload: {
      role: 'STUDENT' | 'ADMIN' | 'SCHOOL_ADMIN';
      schoolId?: string;
      schoolRole?: SchoolRoleValue;
    },
  ): Promise<UserListItem> => {
    const response = await apiFetchWithRetry(`/v1/dashboard/admins/users/${encodeURIComponent(id)}/role`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      let message = `Failed to update role (${response.status})`;
      try {
        const data = (await response.json()) as { message?: string };
        if (data?.message) message = data.message;
      } catch {
        // Ignore parse errors; fall back to the generic message above.
      }
      throw new Error(message);
    }
    return (await response.json()) as UserListItem;
  },

  /**
   * Block (SUSPENDED) / unblock (ACTIVE) / soft-delete (DELETED) a user.
   * Enforced server-side by the auth guard; SUSPENDED/DELETED also
   * Clerk-bans + revokes live sessions. Server rejects self-block and
   * last-active-admin. clerkSync on the result flags a stale Clerk binding.
   */
  updateUserStatus: async (id: string, status: 'ACTIVE' | 'SUSPENDED' | 'DELETED'): Promise<UserListItem> => {
    const response = await apiFetchWithRetry(`/v1/dashboard/admins/users/${encodeURIComponent(id)}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    if (!response.ok) {
      let message = `Failed to update status (${response.status})`;
      try {
        const data = (await response.json()) as { message?: string };
        if (data?.message) message = data.message;
      } catch {
        // Ignore parse errors; fall back to the generic message above.
      }
      throw new Error(message);
    }
    return (await response.json()) as UserListItem;
  },

  /**
   * Repoint a user's auth_user_id to their current Clerk User ID — for a
   * recreated/duplicate Clerk identity (the admin-action form of the manual
   * relink SQL). Server collision-guards on the unique auth_user_id.
   */
  relinkClerk: async (id: string, clerkUserId: string): Promise<UserListItem> => {
    const response = await apiFetchWithRetry(`/v1/dashboard/admins/users/${encodeURIComponent(id)}/relink-clerk`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clerkUserId }),
    });
    if (!response.ok) {
      let message = `Failed to relink Clerk identity (${response.status})`;
      try {
        const data = (await response.json()) as { message?: string };
        if (data?.message) message = data.message;
      } catch {
        // Ignore parse errors; fall back to the generic message above.
      }
      throw new Error(message);
    }
    return (await response.json()) as UserListItem;
  },
};
